"use client";

import { useState, useEffect } from "react";
import { api, type ApprofondimentoDto } from "@/lib/api";
import { categoryColor, categoryAccent } from "@/lib/category-colors";
import { ScrollReveal } from "@/components/ScrollReveal";

interface ApprofondimentiGridBlockProps {
  title: string;
  maxItems?: number;
  columns?: number;
  showArchiveLink?: boolean;
  archiveLinkUrl?: string;
  archiveLinkText?: string;
  className?: string;
}

export function ApprofondimentiGridBlock({
  title,
  maxItems = 6,
  columns = 3,
  showArchiveLink = true,
  archiveLinkUrl = "/approfondimenti",
  archiveLinkText = "Tutti →",
  className,
}: ApprofondimentiGridBlockProps) {
  const [items, setItems] = useState<ApprofondimentoDto[] | null>(null);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const res = await api.approfondimenti.list({ size: maxItems });
        if (!cancelled) setItems(res.items);
      } catch {
        if (!cancelled) setItems([]);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [maxItems]);

  if (items === null) {
    return (
      <section className={`bg-neutral-50/80 ${className || ""}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-8 py-16">
          <div className="h-8 w-56 bg-neutral-200 rounded animate-pulse mb-10" />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="bg-neutral-200 rounded-xl h-40 animate-pulse" />
            ))}
          </div>
        </div>
      </section>
    );
  }

  if (items.length === 0) return null;

  const gridCols = columns === 2
    ? "grid-cols-1 sm:grid-cols-2"
    : columns === 4
      ? "grid-cols-1 sm:grid-cols-2 lg:grid-cols-4"
      : "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3";

  return (
    <section className={`bg-neutral-50/80 ${className || ""}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-8 py-16">
        <ScrollReveal>
          <div className="flex items-end justify-between mb-10">
            <div>
              <h2 className="text-2xl sm:text-3xl font-bold text-[#1a3a5c] uppercase tracking-wide">
                {title}
              </h2>
              <div className="mt-3 h-[3px] w-16 bg-[#4a90c4] rounded-full" />
            </div>
            {showArchiveLink && archiveLinkUrl && (
              <a
                href={archiveLinkUrl}
                className="text-sm font-semibold text-[#4a90c4] hover:text-[#1a3a5c] transition-colors uppercase tracking-wide"
              >
                {archiveLinkText}
              </a>
            )}
          </div>
        </ScrollReveal>
        <div className={`grid ${gridCols} gap-6 sm:gap-8`}>
          {items.slice(0, maxItems).map((a, i) => {
            const mainCat = a.categories?.find((c) => c.slug !== "approfondimenti");
            return (
              <ScrollReveal key={a.id} delay={i * 100}>
                <article className="group bg-white rounded-xl overflow-hidden shadow-sm hover:shadow-lg transition-all duration-300 border border-neutral-100 flex flex-col h-full">
                  <div className={`h-1.5 bg-gradient-to-r ${categoryAccent(mainCat?.slug)}`} />
                  <div className="p-4 sm:p-5 flex flex-col flex-1">
                    {a.categories && a.categories.length > 0 && (
                      <div className="flex gap-1.5 flex-wrap mb-3">
                        {a.categories
                          .filter((c) => c.slug !== "approfondimenti")
                          .map((c) => (
                            <span
                              key={c.id}
                              className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${categoryColor(c.slug)}`}
                            >
                              {c.name}
                            </span>
                          ))}
                      </div>
                    )}
                    <a href={`/approfondimenti/${a.slug}`} className="group/link">
                      <h3 className="font-bold text-[#1a3a5c] group-hover/link:text-[#4a90c4] transition-colors leading-snug line-clamp-3 mb-2">
                        {a.titolo}
                      </h3>
                    </a>
                    <div className="mt-auto pt-3 text-sm text-neutral-500">
                      {new Date(a.data).toLocaleDateString("it-IT", {
                        day: "numeric",
                        month: "long",
                        year: "numeric",
                      })}
                      {a.fonte && <> &mdash; {a.fonte}</>}
                      {a.autori && a.autori.length > 0 && (
                        <> &mdash; di {a.autori.join(", ")}</>
                      )}
                    </div>
                  </div>
                </article>
              </ScrollReveal>
            );
          })}
        </div>
      </div>
    </section>
  );
}

